import aiagentApiService from "./apiService";
import { AIAGENT_ENDPOINTS } from "../endpoints";
import { getStoredUserId } from "../../utils/user";

// TODO: replace with the real salesperson_id once korres has an ERP-relations
// endpoint (api/OfflineSync/GetCompanyErpRelationsList) to resolve one. This app
// has no salesperson concept — it's a fixed placeholder so the backend's
// required field is satisfied.
const AURA_SALESPERSON_ID = "korres-influencer-portal";
const customerId = () => String(getStoredUserId() ?? "");

const relativeTime = (isoString) => {
    const date = new Date(isoString);
    if (!isoString || Number.isNaN(date.getTime())) return "";

    const diffMinutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (diffMinutes < 1) return "Now";
    if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    const diffDays = Math.round(diffHours / 24);
    return diffDays === 1 ? "Yesterday" : `${diffDays} days ago`;
};

const groupForDate = (isoString) => {
    const date = new Date(isoString);
    if (!isoString || Number.isNaN(date.getTime())) return "Today";

    const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (diffDays <= 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return "Last 7 days";
};

export const sendAuraMessage = async ({ query, sessionId }) => {
    const response = await aiagentApiService.post(AIAGENT_ENDPOINTS.SEND_MESSAGE, {
        query,
        salesperson_id: AURA_SALESPERSON_ID,
        customer_id: customerId(),
        session_id: sessionId || null,
    });
    return response?.data || {};
};

// SSE contract (matches app/sse.py, confirmed against the koulieris reference
// client): frames are separated by a blank line, each frame has an
// "event: <type>" line and a "data: <json>" line. "stage" frames report
// progress and carry session_id once the session is created; "delta" frames
// each carry one content token to append; "final" carries the complete
// content plus chat_id/session_id once the stream ends.
export const streamAuraMessage = async ({ query, sessionId, onDelta, onSessionReady, onFinal }) => {
    const response = await aiagentApiService.stream(AIAGENT_ENDPOINTS.SEND_MESSAGE_STREAM, {
        query,
        salesperson_id: AURA_SALESPERSON_ID,
        customer_id: customerId(),
        session_id: sessionId || null,
    });
    if (!response.ok || !response.body) throw new Error("Stream request failed");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";

        for (const frame of frames) {
            let eventType = "";
            let dataLine = "";
            for (const line of frame.split("\n")) {
                if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                else if (line.startsWith("data: ")) dataLine = line.slice(6);
            }
            if (!dataLine) continue;

            let data;
            try {
                data = JSON.parse(dataLine);
            } catch {
                continue;
            }

            if (eventType === "stage" && data.name === "session_ready" && data.session_id) {
                onSessionReady?.(data.session_id);
            } else if (eventType === "delta" && data.content) {
                onDelta?.(data.content);
            } else if (eventType === "final") {
                onFinal?.({ content: data.content || "", chatId: data.chat_id ?? null, sessionId: data.session_id || sessionId || null });
            }
        }
    }
};

export const getStarterQuestions = async () => {
    const response = await aiagentApiService.get(AIAGENT_ENDPOINTS.STARTER_QUESTIONS, {
        params: { is_active: true },
    });
    const data = response?.data;
    return Array.isArray(data) ? data : (data?.data || []);
};

export const getAuraSessions = async () => {
    const response = await aiagentApiService.get(AIAGENT_ENDPOINTS.SESSIONS, {
        params: {
            salesperson_id: AURA_SALESPERSON_ID,
            customer_id: customerId(),
            page: 1,
            page_size: 20,
            time_filter: "all",
        },
    });
    const data = response?.data;
    const list = Array.isArray(data) ? data : (data?.data || []);

    return list.map((item) => {
        const sessionId = item.session_id;
        const updatedAt = item.last_chat_time || item.session_created_at;
        return {
            id: sessionId,
            aiSessionId: sessionId,
            title: item.display_title || item.salesperson_first_question || "Untitled conversation",
            time: relativeTime(updatedAt),
            group: groupForDate(updatedAt),
            pinned: Boolean(item.is_pinned),
            archived: false,
            messageCount: item.total_messages ?? 0,
        };
    });
};

export const getAuraSessionChats = async (sessionId) => {
    const response = await aiagentApiService.get(AIAGENT_ENDPOINTS.SESSION_CHATS(sessionId), {
        params: { page: 1, page_size: 50 },
    });
    const messages = response?.data?.data?.messages || [];

    return messages.map((item) => ({
        id: `chat-${item.id}`,
        role: item.role === "user" ? "user" : "assistant",
        content: item.content || "",
        chatId: item.id,
        sessionId: item.session_id || sessionId,
        feedback: item.feedback || null,
    }));
};

export const renameAuraSession = async (sessionId, title) => {
    await aiagentApiService.patch(AIAGENT_ENDPOINTS.SESSION(sessionId), {
        title,
        salesperson_id: AURA_SALESPERSON_ID,
    });
};

export const pinAuraSession = async (sessionId, isPinned) => {
    await aiagentApiService.patch(AIAGENT_ENDPOINTS.SESSION_PIN(sessionId), {
        is_pinned: isPinned,
        salesperson_id: AURA_SALESPERSON_ID,
    });
};

export const deleteAuraSession = async (sessionId) => {
    await aiagentApiService.delete(AIAGENT_ENDPOINTS.SESSION(sessionId), {
        params: { salesperson_id: AURA_SALESPERSON_ID },
    });
};

export const sendAuraFeedback = async ({ chatId, sessionId, isPositive, issueTypeId = null, comment = "" }) => {
    await aiagentApiService.post(AIAGENT_ENDPOINTS.FEEDBACK, {
        chat_id: chatId,
        session_id: sessionId,
        is_positive: isPositive,
        issue_type_id: issueTypeId != null ? Number(issueTypeId) : null,
        comment: comment.trim() || null,
    });
};

// Reference data — documented as empty for korres until seeded from koulieris,
// so an empty list here is expected, not a failure.
export const getFeedbackIssueTypes = async () => {
    const response = await aiagentApiService.get(AIAGENT_ENDPOINTS.FEEDBACK_ISSUE_TYPES, {
        params: { is_active: true },
    });
    const data = response?.data;
    const list = Array.isArray(data) ? data : (data?.data || []);

    return list.map((item) => (typeof item === "string"
        ? { id: item, label: item }
        : {
            id: item.id ?? item.issue_type_id,
            label: item.label || item.name || item.title || String(item.id ?? item.issue_type_id),
        }));
};

export const sendAuraSurvey = async ({ sessionId, chatId, ...answers }) => {
    await aiagentApiService.post(AIAGENT_ENDPOINTS.FEEDBACK_SURVEY, {
        session_id: sessionId,
        chat_id: chatId,
        ...answers,
    });
};
